import create from '../creator'
import axios from '../../axios-setup'
import { PRODUCTS_FILTER_Values } from '@/constants'
import { keyBy, flatMap, some } from 'lodash'

const initialState = () => ({
	catalog: [],
	isCatalogLoading: false,
	filter: null,
	trending: 0,
	productSearch: '',
	lastNonEmptyProductSearch: ''
})

const FILTER_CONFIG = {
	favorite: {
		flagKey: PRODUCTS_FILTER_Values.favorite,
		posKey: `${PRODUCTS_FILTER_Values.favorite}Position`
	},
	new: {
		flagKey: PRODUCTS_FILTER_Values.new,
		posKey: `${PRODUCTS_FILTER_Values.new}ProductPosition`
	}
}

function filterAndSortByFlag(list, { flagKey, posKey }) {
	const only = list.filter(el => el?.[flagKey] === true)
	return only.sort((a, b) => {
		const ap = a?.[posKey]
		const bp = b?.[posKey]
		if (ap != null && bp != null && ap !== bp) return ap - bp
		if (ap != null && bp == null) return -1
		if (ap == null && bp != null) return 1
		return (a?.position ?? 0) - (b?.position ?? 0)
	})
}

export function matchesCharsInOrder(targetString, searchTerm) {
	if (!searchTerm) return true
	const normalizedSearch = searchTerm.toLowerCase().replace(/\s+/g, '')
	const normalizedTarget = String(targetString || '').toLowerCase()
	if (!normalizedSearch) return true
	let currentIndex = 0
	return [...normalizedSearch].every(character => {
		const foundIndex = normalizedTarget.indexOf(character, currentIndex)
		if (foundIndex === -1) return false
		currentIndex = foundIndex + 1
		return true
	})
}

function filterBySearch(products, searchTerm) {
	const normalizedSearch = (searchTerm || '').toLowerCase().replace(/\s+/g, '')
	if (!normalizedSearch) {
		return products
	}
	return products.filter(product =>
		matchesCharsInOrder(product?.name, normalizedSearch)
	)
}

function sortByPositionAsc(a, b) {
	const positionA = a?.position ?? 0
	const positionB = b?.position ?? 0
	return positionA - positionB
}

function catalogHasAnyMatch(catalog, query) {
	if (query === '' || catalog.length === 0) return false
	return catalog.some(category => {
		if (!category) return false
		return category.products.some(product => {
			const productName = product ? product.name : ''
			return matchesCharsInOrder(productName, query)
		})
	})
}

function getSubsequenceMatchMetrics(targetString, searchTerm) {
	const normalizedQuery = searchTerm.toLowerCase().replace(/\s+/g, '')
	const normalizedTarget = targetString.toLowerCase()
	if (!normalizedQuery) return null
	const matchPositions = []
	let searchFromIndex = 0
	for (const char of normalizedQuery) {
		const nextIndex = normalizedTarget.indexOf(char, searchFromIndex)
		if (nextIndex === -1) return null
		matchPositions.push(nextIndex)
		searchFromIndex = nextIndex + 1
	}
	const firstIndex = matchPositions[0]
	const lastIndex = matchPositions[matchPositions.length - 1]
	const spanLength = lastIndex - firstIndex + 1
	let totalGaps = 0
	for (let i = 1; i < matchPositions.length; i++) {
		// Characters between matched letters
		totalGaps += matchPositions[i] - matchPositions[i - 1] - 1
	}
	return { gaps: totalGaps, start: firstIndex, span: spanLength }
}

function compareBySearchRelevance(a, b, query) {
	const aMetrics = getSubsequenceMatchMetrics(a.name, query)
	const bMetrics = getSubsequenceMatchMetrics(b.name, query)
	// Cases when one or both items do not match the search term
	if (aMetrics && !bMetrics) return -1
	if (!aMetrics && bMetrics) return 1
	if (!aMetrics && !bMetrics) return 0
	// Compare by number of gaps between matched letters less gaps is better
	if (aMetrics.gaps !== bMetrics.gaps) return aMetrics.gaps - bMetrics.gaps
	// If gaps are equal then prefer start earlier in the word
	if (aMetrics.start !== bMetrics.start) return aMetrics.start - bMetrics.start
	// If sorting equal prefer matches with shorter span
	if (aMetrics.span !== bMetrics.span) return aMetrics.span - bMetrics.span
	// Prefer shorter names
	const aNameLen = a.name.length
	const bNameLen = b.name.length
	if (aNameLen !== bNameLen) return aNameLen - bNameLen
	return sortByPositionAsc(a, b)
}

const state = initialState()

const actions = {
	async fetchCatalog({ commit, rootState }) {
		commit('setCatalogLoading', true)
		try {
			const { id: venueId } = rootState.venue.venueInfo
			const { data = [] } = await axios.get('category', { params: { venueId } })
			const catalog = data.sort(sortByPositionAsc).map(category => ({
				...category,
				products: [...category.products].sort(sortByPositionAsc)
			}))
			commit('setCatalogData', catalog)
		} finally {
			commit('setCatalogLoading', false)
		}
	},

	changeProductSearch({ commit }, value) {
		commit('changeProductSearch', value)
	},

	syncRouteFilters({ commit }, { filter, trending } = {}) {
		commit('setFilters', { filter, trending })
	}
}

const mutations = {
	setCatalogLoading(state, isLoading) {
		state.isCatalogLoading = isLoading
	},
	setCatalogData(state, catalog) {
		state.catalog = catalog
	},
	setFilters(state, { filter, trending }) {
		if (typeof filter !== 'undefined') state.filter = filter
		if (typeof trending !== 'undefined') state.trending = Number(trending)
		if (
			(filter && filter !== PRODUCTS_FILTER_Values.all) ||
			Number(trending) === 1
		) {
			state.productSearch = ''
		}
	},
	changeProductSearch(state, newSearchValue) {
		state.productSearch = newSearchValue
		let trimmedQuery = ''
		if (newSearchValue != null) {
			trimmedQuery = newSearchValue.toString().trim()
		}
		if (trimmedQuery === '') return
		if (catalogHasAnyMatch(state.catalog, trimmedQuery)) {
			state.lastNonEmptyProductSearch = trimmedQuery
		}
	}
}

const getters = {
	categoryMap(state) {
		return keyBy(state.catalog, 'name')
	},

	orderedCategoryNames(state) {
		return state.catalog.map(category => category.name)
	},

	notEmptyCategories(state, getters) {
		const categoriesWithVisibleProducts = state.catalog.map(
			({ name, ...rest }) => {
				const products = getters.productsOf(name)
				return { ...rest, name, products }
			}
		)
		const hasVisibleProducts = category =>
			Array.isArray(category.products) && category.products.length > 0
		const nonEmpty = categoriesWithVisibleProducts.filter(hasVisibleProducts)
		return nonEmpty.sort(sortByPositionAsc)
	},

	nonEmptyCategoryNames(state, getters) {
		return getters.notEmptyCategories.map(category => category.name)
	},

	isCatalogLoading(state) {
		return state.isCatalogLoading
	},

	isEmptySearchResult(state) {
		const query = state.productSearch
		if (!query) return false
		const categoryHasMatch = category =>
			Array.isArray(category?.products) &&
			category.products.some(product =>
				matchesCharsInOrder(product?.name, query)
			)
		return !state.catalog.some(categoryHasMatch)
	},

	lastValidProductSearch(state) {
		const rawQuery = (state.productSearch || '').trim()
		if (!rawQuery) return ''
		const categoryContainsMatch = (category, needle) =>
			Array.isArray(category?.products) &&
			category.products.some(product =>
				matchesCharsInOrder(product?.name, needle)
			)
		const hasAnyMatch = needle =>
			state.catalog.some(category => categoryContainsMatch(category, needle))
		if (hasAnyMatch(rawQuery)) return rawQuery
		const prefixesFromLongestToShortest = []
		let currentPrefix = rawQuery
		while (currentPrefix.length > 1) {
			currentPrefix = currentPrefix.slice(0, -1)
			prefixesFromLongestToShortest.push(currentPrefix)
		}
		const matchedPrefix = prefixesFromLongestToShortest.find(hasAnyMatch)
		return matchedPrefix || ''
	},

	productSearchValue(state) {
		return state.productSearch
	},

	categoryNameByProductId: state => productId => {
		if (productId == null) return null
		const categoryContainsProduct = category =>
			Array.isArray(category?.products) &&
			category.products.some(product => product?.id === productId)
		const category = state.catalog.find(categoryContainsProduct)
		return category?.name ?? null
	},

	categoryByName: (state, getters) => name => getters.categoryMap[name] || null,

	effectiveProductSearch(state, getters) {
		const currentQuery = state.productSearch
		if (currentQuery === '') return ''
		if (getters.isEmptySearchResult) {
			// If no previous valid search, return empty string - shows everything
			return state.lastNonEmptyProductSearch || ''
		}
		return currentQuery
	},

	productsOf: (state, getters) => categoryName => {
		const category = getters.categoryByName(categoryName)
		if (!category || !Array.isArray(category.products)) return []
		const products = [...category.products]
		const selectedFilterConfig = FILTER_CONFIG[state.filter]
		if (selectedFilterConfig) {
			// favorite and new
			return filterAndSortByFlag(products, selectedFilterConfig)
		}
		if (state.trending === 1) {
			// trending
			const compareByTrendingThenPosition = (a, b) => {
				const ratingA = a && a.trendingCategoryRating
				const ratingB = b && b.trendingCategoryRating
				const hasA = ratingA != null
				const hasB = ratingB != null
				if (hasA && hasB && ratingA !== ratingB) return ratingA - ratingB
				if (hasA && !hasB) return -1
				if (!hasA && hasB) return 1
				return sortByPositionAsc(a, b)
			}
			return products
				.filter(product => product?.trending === true)
				.sort(compareByTrendingThenPosition)
		}
		// Regular + search
		const searchTerm = getters.effectiveProductSearch
		const productsMatchingSearch = filterBySearch(products, searchTerm)
		if (!searchTerm) {
			// No search keep original position sorting
			return productsMatchingSearch.sort(sortByPositionAsc)
		}
		// Searching sort by subsequence relevance
		return productsMatchingSearch.sort((a, b) =>
			compareBySearchRelevance(a, b, searchTerm)
		)
	},

	filter(state) {
		return state.filter
	},

	hasProductsWithFlag: state => flagKey => {
		const allProducts = flatMap(state.catalog, category => category.products)
		return some(allProducts, product => product[flagKey] === true)
	},

	isNewProductPresented: (state, getters) => {
		return getters.hasProductsWithFlag(FILTER_CONFIG.new.flagKey)
	},

	isStaffFavoriteProductPresented: (state, getters) => {
		return getters.hasProductsWithFlag(FILTER_CONFIG.favorite.flagKey)
	}
}

export default create(
	{
		namespaced: true,
		state,
		actions,
		mutations,
		getters
	},
	initialState()
)
